import { Badge } from '@/components/common/badge'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/common/table'
import { TabsContent } from '@/components/common/tabs'
import type { TransactionsContext } from '@/queries/transactionsContext.query'
import type { StepData } from '@/types/transactionsContext'
import type { Vault } from '@/types/vault'
import { fromUnixTimestamp } from '@vaultsfyi/common/utils'
import { formatDate, formatTokenValue } from '@vaultsfyi/common/utils'
import { useMemo } from 'react'
import type { useForm } from 'react-hook-form'
import { type Address, formatUnits } from 'viem'
import { z } from 'zod'
import { TxSteps } from '../sidePanel/txSteps'

const ClaimStatus = {
  READY: 'ready',
  PENDING: 'pending',
} as const

const claimDataSchema = z
  .object({
    cooldownEnd: z.string(),
    assetAmount: z.string(),
    status: z.nativeEnum(ClaimStatus),
  })
  .optional()

interface Props {
  account: Address | undefined
  stepData: StepData
  onSubmit: () => void
  isSubmitLoading: boolean
  form: ReturnType<typeof useForm>
  vault: Vault
  transactionsContext: TransactionsContext
  currentStep: number
}

export function EthenaWithdrawalTab({
  account,
  stepData,
  onSubmit: _onSubmit,
  isSubmitLoading,
  form,
  vault,
  transactionsContext,
  currentStep,
}: Props) {
  const claimData = useMemo(() => {
    return claimDataSchema.parse((transactionsContext?.vaultSpecificData as Record<string, any>)['claimData'])
  }, [transactionsContext])

  const onSubmit = () => {
    form.setValue('amount', formatUnits(BigInt(claimData?.assetAmount ?? '0'), vault.asset.decimals), {
      shouldValidate: true,
    })
    _onSubmit()
  }

  const isClaimable = claimData?.status === ClaimStatus.READY

  return (
    <form onSubmit={onSubmit}>
      <TabsContent value={stepData.name} className="mt-3 flex flex-col gap-3 md:mt-4 md:gap-4">
        {claimData ? (
          <Table>
            <TableHeader>
              <TableRow variant="header">
                <TableHead>Amount</TableHead>
                <TableHead>Date</TableHead>
                <TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              <TableRow>
                <TableCell>
                  {formatTokenValue({ value: claimData.assetAmount, decimals: vault.asset.decimals })}{' '}
                  {vault.asset.symbol}
                </TableCell>
                <TableCell>
                  {formatDate({
                    timestamp: fromUnixTimestamp(Number(claimData.cooldownEnd)),
                    showYear: true,
                    showTime: true,
                  })}
                </TableCell>
                <TableCell>
                  <Badge color={claimData.status === 'pending' ? 'orange' : 'green'}>{claimData.status}</Badge>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        ) : (
          <p className="text-xs">No claimable withdrawals</p>
        )}
        <TxSteps
          account={account}
          stepData={stepData}
          activeStep={currentStep}
          disabled={!isClaimable}
          loading={isSubmitLoading}
          label="Claim withdrawal"
          network={vault.network.name}
        />
      </TabsContent>
    </form>
  )
}
